import {Alert, Typography} from "@mui/material";
import React, {createContext, PropsWithChildren, useContext, useEffect, useMemo, useState} from "react";
import {useAsync} from "react-async-hook";
import {useNavigate} from "react-router-dom";
import {B2Tenant} from "../data/client.gen";
import {useTenantApi} from "../data/tenants";
import {notBlank} from "../utils";

interface TenantContextProps {
	tenant: B2Tenant | undefined;
	setTenant: (tenant: B2Tenant) => void;
}

const TenantContext = createContext<TenantContextProps>({
	tenant: undefined,
	setTenant: () => {
		throw new Error("Context not properly initialized");
	},
});

function guessTenantId(): string {
	const host = window.location.hostname;
	switch (host) {
		case "booking.citylocker.at":
			return "citylocker";
		case "booking.citylocker.store":
			return "citylocker-store";
		case "balzklub.easylock.ch":
			return "balzklub";
		case "booking.citylockers.al":
		case "booking-dev.citylockers.al":
			return "city-lockers-ltd";
		case "booking.holdmybag.net":
			const pathname = window.location.pathname;
			if (pathname.startsWith(`/in-stuttgart`)) {
				return "in-stuttgart";
			}
			else if (pathname.startsWith("/d-live-duesseldorf")) {
				return "d-live-duesseldorf";
			}
			else if (pathname.startsWith("/acama-kreuzberg")) {
				return "acama-kreuzberg";
			}
			else if (pathname.startsWith("/elenas-apartments")) {
				return "elenas-apartments";
			}
	}
	return "variocube";
}

function TenantContextProvider({children}: PropsWithChildren<{}>) {
	const navigate = useNavigate();
	const {getTenant} = useTenantApi();

	const [tenant, setTenant] = useState<B2Tenant>();

	const value = useMemo(() => ({tenant, setTenant}), [tenant, setTenant]);

	useEffect(() => {
		if (window.location.pathname === "/" || window.location.pathname === "") {
			navigate(`/${guessTenantId()}`);
		}
	}, []);

	const {result, error} = useAsync(async () => {
		try {
			const tenantId = window.location.pathname.split("/")[1];
			return (notBlank(tenantId))
				? getTenant(tenantId as string)
				: undefined;
		}
		catch (err) {
			console.error("Failed to load tenant", err);
			return undefined;
		}
	}, []);

	useEffect(() => {
		if (result) {
			setTenant(result);
		}
	}, [result]);

	// Render children immediately so route components can start their own data fetches in parallel
	// with `getTenant` (notably StorefrontPage, which doesn't need tenant data at all). BrandingBar,
	// AppThemeProvider and SiteList all already handle `tenant === undefined` defensively.
	return (
		<TenantContext.Provider value={value}>
			{error && (
				<>
					<Typography variant="h5">Error loading tenant configuration.</Typography>
					<Alert severity="error">{error?.message ?? "Error"}</Alert>
				</>
			)}
			{!error && children}
		</TenantContext.Provider>
	);
}

const useTenant = () => useContext(TenantContext);

export { TenantContextProvider, useTenant };
